import React, { useState } from "react";
import { X, Send, Loader2, Receipt, ShieldCheck, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { CustomerCombobox } from "./CustomerCombobox";

/**
 * Bundle & Send — email Tax Invoice + Claim Form + Job Card PDFs
 * to an insurer (or any recipient) in a single Resend call.
 */
export const BundleSendModal = ({ job, onClose }) => {
  const hasInsurance = !!(job.insurance_company || job.policy_number || job.claim_number);
  const veh = `${job.vehicle_make_model || ""} ${job.registration || ""}`.trim() || "vehicle";

  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [picked, setPicked] = useState(null);
  const [subject, setSubject] = useState(
    `Claim documents — Job #${job.job_number} — ${veh}${job.claim_number ? ` (Claim ${job.claim_number})` : ""}`,
  );
  const [message, setMessage] = useState("");
  const [includeInvoice, setIncludeInvoice] = useState(true);
  const [includeClaim, setIncludeClaim] = useState(hasInsurance);
  const [includeJobcard, setIncludeJobcard] = useState(true);
  const [busy, setBusy] = useState(false);

  const parseList = (s) =>
    s.split(/[,;\s]+/).map((x) => x.trim()).filter((x) => /@/.test(x));

  const send = async () => {
    const toList = parseList(to);
    if (toList.length === 0) {
      toast.error("Enter at least one recipient email");
      return;
    }
    if (!includeInvoice && !includeClaim && !includeJobcard) {
      toast.error("Pick at least one document to attach");
      return;
    }
    setBusy(true);
    try {
      const res = await adminApi.bundleSend(job.id, {
        to: toList,
        cc: parseList(cc),
        subject: subject.trim() || null,
        message: message.trim() || null,
        include_invoice: includeInvoice,
        include_claim_form: includeClaim,
        include_jobcard: includeJobcard,
      });
      if (res.ok) {
        const attached = (res.included || []).length;
        const skipped = (res.skipped || []).length;
        toast.success(
          `Sent ${attached} document${attached === 1 ? "" : "s"} to ${toList.join(", ")}` +
            (skipped ? ` (${skipped} skipped)` : "") +
            (res.used_sandbox ? " · from Resend sandbox" : ""),
        );
        onClose();
      } else {
        // Even when Resend fails, the PDFs were successfully generated — surface both facts
        const attached = (res.included || []).length;
        if (attached > 0) {
          toast.warning(
            `Documents prepared (${attached}) but Resend rejected the send: ${res.error || "unknown error"}. ${res.hint || "Check Admin → Settings."}`,
            { duration: 8000 },
          );
        } else {
          toast.error(res.error || "Send failed");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="bundle-send-modal"
    >
      <div className="bg-white rounded-md shadow-xl max-w-xl w-full p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">
              Bundle &amp; Send
            </div>
            <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">
              Email documents to insurer
            </h2>
            <p className="text-xs text-[#3f4b52] mt-1">
              Job #{job.job_number} · {job.client_name || "—"} · {veh}
            </p>
          </div>
          <button onClick={onClose} className="text-[#3f4b52]" aria-label="Close" data-testid="bundle-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          {/* Address book */}
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              Look up customer / insurer
            </label>
            <div className="mt-1">
              <CustomerCombobox
                value={picked}
                onChange={(c) => {
                  setPicked(c);
                  if (c && c.email) setTo((prev) => prev ? `${prev}, ${c.email}` : c.email);
                }}
                testid="bundle-customer-picker"
                placeholder="Type name or code to auto-fill To…"
              />
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              To (comma-separated)
            </label>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="claims@insurer.co.za"
              className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
              data-testid="bundle-to-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              CC (optional)
            </label>
            <input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="broker@example.com"
              className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
              data-testid="bundle-cc-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              Subject
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
              data-testid="bundle-subject-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              Message (optional — replaces default intro)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Add a personal note to the insurer…"
              className="w-full mt-1 px-3 py-2 border border-[#cfd7db] rounded-md text-sm"
              data-testid="bundle-message-input"
            />
          </div>

          {/* Attachments picker */}
          <div className="border border-[#cfd7db] rounded-md p-3 bg-[#f5f6f6]">
            <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mb-2">
              Attach
            </div>
            <div className="space-y-2">
              <AttachRow
                icon={Receipt}
                label="Tax Invoice"
                sub={job.tax_invoice_number ? `#${job.tax_invoice_number}` : "generated on demand"}
                checked={includeInvoice}
                onChange={setIncludeInvoice}
                testid="bundle-attach-invoice"
              />
              <AttachRow
                icon={ShieldCheck}
                label="Insurance Claim Form"
                sub={
                  hasInsurance
                    ? `${job.insurance_company || "—"} · ${job.claim_number || "no claim no."}`
                    : "no insurance details on job"
                }
                checked={includeClaim}
                onChange={setIncludeClaim}
                disabled={!hasInsurance}
                testid="bundle-attach-claim"
              />
              <AttachRow
                icon={ClipboardList}
                label="Approved Job Card"
                sub="attached only if an approved card exists"
                checked={includeJobcard}
                onChange={setIncludeJobcard}
                testid="bundle-attach-jobcard"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md hover:bg-[#f5f6f6]"
            data-testid="bundle-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={busy}
            data-testid="bundle-send-btn"
            className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {busy ? "Sending…" : "Send bundle"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AttachRow = ({ icon: Icon, label, sub, checked, onChange, disabled, testid }) => (
  <label
    className={`flex items-start gap-3 p-2 rounded-md border ${
      disabled ? "border-transparent opacity-50" : "border-[#e5e7eb] bg-white hover:border-[#f28a16]"
    } cursor-pointer transition`}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 accent-[#f28a16]"
      data-testid={testid}
    />
    <Icon size={16} className="text-[#f28a16] shrink-0 mt-0.5" />
    <div className="min-w-0">
      <div className="text-sm font-bold text-[#111619]">{label}</div>
      <div className="text-[11px] text-[#3f4b52]">{sub}</div>
    </div>
  </label>
);

export default BundleSendModal;
